import numeral from "numeral";
import React, { useContext, useEffect, useMemo, useState } from "react";

import Plot from "react-plotly.js";
import { getRoseData } from "../api/data";
import { ExploreContext } from "../context/explore";
import { getExploreColumnKeys } from "../helpers/core";
import { ErrorMessage, WarningMessage } from "./error-message";
import { ExploreSelect } from "./explore-select";
import Loader from "./loader";


interface ExploreRosePlotProps {
  loading: boolean;
}
export const ExploreRosePlot = ({
  loading,
}: ExploreRosePlotProps) => {
  const [error, setError] = useState<string[] | null>(null)
  const [warning, setWarning] = useState<string[] | null>(null)
  const [dataLoading, setDataLoading] = useState<boolean>(false)
  const [plotData, setPlotData] = useState<any[]>([])
  const [radialAxisAngle, setRadialAxisAngle] = useState<number>(0)
  const [dtick, setDtick] = useState<number>(2)

  const [keyOne, setKeyOne] = useState<string | null>(null);
  const [keyTwo, setKeyTwo] = useState<string | null>(null);
  const [keyThree, setKeyThree] = useState<string | null>(null);
  const [keyFour, setKeyFour] = useState<string | null>(null);

  const { 
    isPurchasedPoint,
    exploreDataSchema,
  } = useContext(ExploreContext);

  const {
    latStr, 
    lonStr, 
    model, 
    provider, 
    geography, 
    version, 
    variableOptions, 
    directionOptions, 
    title
  } = useMemo(() => {
    let title = ''
    let latStr = ''
    let lonStr = ''
    let model: null | string = ''
    let provider: null | string = ''
    let geography: null | string = ''
    let version: string | null = ''
    let variableOptions: any[] = []
    let directionOptions: any[] = []
    if (exploreDataSchema) {
      const {
        vectors,
        description: { 
          model: _m, 
          provider: _p, 
          geography: _g, 
          version: _v, 
          lat, 
          lon 
        },
      } = exploreDataSchema;
      const {directional, nonDirectional} = getExploreColumnKeys(
        exploreDataSchema,
        'split'
      )
      directionOptions = directional
      variableOptions = nonDirectional

      model = _m === 'null' ? null : _m
      provider = _p === 'null' ? null : _p 
      geography = _g === 'null' ? null : _g
      version = _v === 'null' ? null : _v
      latStr = numeral(lat).format("0.00000");
      lonStr = numeral(lon).format("0.00000");
      
      let found = vectors.find((v: any) => v.key === keyOne)
      if (found) {
        title = `${found.name}` 
      }
      found = vectors.find((v: any) => v.key === keyTwo)
      if (found) {
        title = `${title} - ${found.name}` 
      }
    }
    return {
      latStr,
      lonStr,
      model,
      provider,
      geography,
      version,
      variableOptions,
      directionOptions,
      title,
    };
  }, [exploreDataSchema, keyOne, keyTwo]);

  useEffect(() => {
    setError(null)
    setWarning(null)
    const execute = async (preview: boolean, filename: string) => {
      setDataLoading(true)
      const result = await getRoseData({
        magnitude: keyOne,
        direction: keyTwo,
        granularity: Number(keyFour) || 12,
        time: keyThree || 'All',
        preview,
        filename,
        orderId: exploreDataSchema?.description?.orderId || null,
        model,
        provider,
        geography,
        version
      })
      const {error, response} = result

      if (error === 200 && response) {
        const {plotData, units, radialAxisAngle, frequencyIncrement} = response
        if (plotData.length === 0) {
          setWarning([`Selected vector **(${keyOne})** cannot be rendered as a rose plot for given location.`])
        } else {
          setPlotData(plotData.map((item) => {
            const {color, label, values} = item
            return {
              r: [...values],
              marker: { color },
              type: "barpolar",
              showlegend: true,
              legendgrouptitle: {
                text: `Units: ${units}`,
              },
              mode: "markers",
              name: `${label}`,
              angularaxis: { direction: "clockwise" },
              hovertemplate: `${label}`,
              hoverinfo: "r",
            };
          }))
          setDtick(frequencyIncrement || 5)
          setRadialAxisAngle(radialAxisAngle)
        }
      } else {
        setError([`Problem loading rose plot data for **${keyOne}**, **${keyTwo}**, **${keyThree}** and direction of **${keyFour}**`])
      }
      setDataLoading(false)
    }
    if (exploreDataSchema) {
      const {
        description: { filename, uid },
      } = exploreDataSchema;
      const preview = !!(uid && `${uid}`.indexOf('preview') !== -1)
      if (keyOne && keyTwo && keyFour) {
        execute(preview, filename)
      }
    }
  }, [keyOne, keyTwo, keyThree, keyFour, exploreDataSchema, model, provider, geography, version ]);

  const isLoading =
    keyOne === null
    || keyTwo === null
    || exploreDataSchema === null
    || loading
    || dataLoading;

  return (
    <>
      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "flex-start", "width": "100%" }}>
        <ExploreSelect
          title="Variable"
          columnKeys={variableOptions}
          onChangeKey={(key) => {
            if (key && Array.isArray(key)) {
              setKeyOne(key[0]);
            } else {
              setKeyOne(null);
            }
          }}
        />
        <ExploreSelect
          title="Direction"
          columnKeys={directionOptions}
          onChangeKey={(key)=>{
            if (key && Array.isArray(key)) {
              setKeyTwo(key[0]);
            } else {
              setKeyTwo(null);
            }
          }} 
        />
        <ExploreSelect
          title="Time"
          columnKeys={[
            "All",
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ]}
          onChangeKey={(key)=>{
            if (key && Array.isArray(key)) {
              setKeyThree(key[0]);
            } else {
              setKeyThree(null);
            }
          }} 
        />
        <ExploreSelect
          title="No. of Directions"
          columnKeys={["4", "8", "12", "16", "32", "36"]}
          initialValue={2}
          onChangeKey={(key)=>{
            if (key && Array.isArray(key)) {
              setKeyFour(key[0]);
            } else {
              setKeyFour(null);
            }
          }} 
        />
      </div>
      {isLoading ? (
        <div style={{ width: "100%", padding: "240px 0 64px 0" }}>
          <Loader center showCog />
        </div>
      ) : (
        <>
        {warning && <WarningMessage message={warning} />}
        {error && <ErrorMessage message={error} />}
        {(error === null && warning === null) && 
          <div
            style={{
              height: "550px",
              width: "100%",
              textAlign: "center",
            }}
          >
            <Plot
              config={{
                modeBarButtonsToRemove: ['zoom2d', 'lasso2d', 'select2d', 'toggleHover', 'toggleSpikelines', 'hoverClosestCartesian', 'hoverCompareCartesian', 'toggleHover', 'toggleSpikelines', 'hoverClosestCartesian', 'hoverCompareCartesian'],
                toImageButtonOptions: {
                  format: "png",
                  filename: `${model}-${keyOne}-${keyTwo}-lat${latStr}-lon${lonStr}`,
                  height: 600,
                  width: 800,
                  scale: 6,
                },
              }}
              data={plotData}
              layout={{
                images: isPurchasedPoint ? undefined :[ {
                  source: "/mow-logo-inline-full.png",
                  xref: "paper",
                  yref: "paper",
                  x: -0.05,
                  y: -0.05,
                  xanchor: "left",
                  yanchor: "top",
                  sizex: 0.4,
                  sizey: 0.4,
                  // sizing: "contain",
                  opacity: 1,
                  layer: "above"
                },],
                dragmode: false,
                height: 600,
                title: {
                  text: `${title}`,
                },
                font: { size: 16 },
                legend: {
                  itemsizing: "constant",
                  font: { size: 14 },
                },
                bargap: 3,
                polar: {
                  // @ts-ignore
                  barmode: "overlay",
                  radialaxis: {
                    fixedrange: true,
                    ticksuffix: "%", 
                    tickangle: (90 - radialAxisAngle + 360) % 360, 
                    angle: (90 - radialAxisAngle + 360) % 360, 
                    dtick 
                  },
                  angularaxis: {
                    fixedrange: true,
                    direction: "clockwise"
                  },
                },
              }}
            />
          </div>
        }
      </>
    )}
  </>
  );
};
